function itemIdFrom(item, idx) {
  return String(item?._id || item?.id || `item-${idx}`);
}

function findBoothInEvent(event, boothId) {
  if (!boothId || !Array.isArray(event?.booths)) return null;
  return event.booths.find((booth, idx) => itemIdFrom(booth, idx) === String(boothId)) || null;
}

function findTicketInEvent(event, ticketId) {
  if (!ticketId || !Array.isArray(event?.tickets)) return null;
  return event.tickets.find((ticket, idx) => itemIdFrom(ticket, idx) === String(ticketId)) || null;
}

function pickItemCapacity(item) {
  if (!item || typeof item !== "object") return 0;
  const candidates = [item.capacity, item.maxCapacity, item.availableCapacity, item.remaining];
  for (const value of candidates) {
    const num = Number(value);
    if (!Number.isNaN(num) && num > 0) return num;
  }
  return 0;
}

function toYmdFromValue(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    const dmyMatch = /^(\d{2})-(\d{2})-(\d{2,4})$/.exec(trimmed);
    if (dmyMatch) {
      const [, day, month, yearRaw] = dmyMatch;
      const year = yearRaw.length === 2 ? `20${yearRaw}` : yearRaw;
      return `${year}-${month}-${day}`;
    }
  }
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function pickEventEndDateYmd(event) {
  if (!event || typeof event !== "object") return "";
  const candidates = [
    event.eventEndDate,
    event.eventenddate,
    event.endDate,
    event.end_date,
    event.event_end_date,
    event.closingDate,
    event.finishDate,
  ];
  for (const raw of candidates) {
    const ymd = toYmdFromValue(raw);
    if (ymd) return ymd;
  }
  return "";
}

export function pickEventStartDateYmd(event) {
  if (!event || typeof event !== "object") return "";
  const candidates = [
    event.eventStartDate,
    event.eventstartdate,
    event.startDate,
    event.start_date,
    event.event_start_date,
    event.date,
    event.eventDate,
  ];
  for (const raw of candidates) {
    const ymd = toYmdFromValue(raw);
    if (ymd) return ymd;
  }
  return "";
}

function pickEventCapacity(event) {
  if (!event || typeof event !== "object") return 0;
  const candidates = [
    event.eventCapacity,
    event.maxGuest,
    event.maxGuests,
    event.maximumGuests,
    event.capacity,
  ];
  for (const value of candidates) {
    const num = Number(value);
    if (!Number.isNaN(num) && num > 0) return num;
  }
  return 0;
}

export function resolveBookingMaxCapacity(event, selectedBooth, boothId, selectedTicket, ticketId) {
  if (boothId) {
    const booth = selectedBooth || findBoothInEvent(event, boothId);
    const boothCap = pickItemCapacity(booth);
    if (boothCap > 0) return boothCap;
  }
  if (ticketId) {
    const ticket = selectedTicket || findTicketInEvent(event, ticketId);
    const ticketCap = pickItemCapacity(ticket);
    if (ticketCap > 0) return ticketCap;
  }
  return pickEventCapacity(event);
}

export function readDateCapacityFromRow(row) {
  if (!row || typeof row !== "object") return null;
  const candidates = [row.availableCapacity, row.capacity, row.remaining, row.available];
  for (const value of candidates) {
    if (value == null || value === "") continue;
    const num = Number(value);
    if (!Number.isNaN(num)) return num;
  }
  return null;
}

export function capacityForBookingDate(ymd, monthCapacityByDate, monthLoading, bookingMaxCapacity) {
  if (monthLoading) return null;
  const capMap = monthCapacityByDate || {};
  const hasApiValue = Object.prototype.hasOwnProperty.call(capMap, ymd);
  if (hasApiValue) {
    return Number(capMap[ymd] ?? 0);
  }
  if (bookingMaxCapacity > 0) return bookingMaxCapacity;
  return 0;
}

export const INVALID_BOOKING_RANGE_MESSAGE =
  "Booking is not available plz select other date";

export function eachBookingRangeYmd(startYmd, endYmd) {
  if (!startYmd) return [];
  const list = [];
  const start = new Date(`${startYmd}T12:00:00`);
  const end = new Date(`${endYmd || startYmd}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [startYmd];
  const cursor = new Date(start);
  while (cursor <= end) {
    const y = cursor.getFullYear();
    const m = String(cursor.getMonth() + 1).padStart(2, "0");
    const d = String(cursor.getDate()).padStart(2, "0");
    list.push(`${y}-${m}-${d}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return list.length ? list : [startYmd];
}

export function isBookingDateUnavailable(
  ymd,
  { monthLoading, monthAvailableDates, monthCapacityByDate, bookingMaxCapacity, isDateAllowed }
) {
  if (!ymd) return true;
  const [y, m, d] = String(ymd).split("-").map(Number);
  if (!y || !m || !d) return true;
  const date = new Date(y, m - 1, d);
  if (typeof isDateAllowed === "function" && !isDateAllowed(date)) return true;
  if (monthLoading) return false;
  const hasApiDates = Object.keys(monthAvailableDates || {}).length > 0;
  if (hasApiDates && !monthAvailableDates[ymd]) return true;
  const cap = capacityForBookingDate(ymd, monthCapacityByDate, false, bookingMaxCapacity);
  if (cap != null && cap <= 0) return true;
  return false;
}

export function getUnavailableDatesInRange(startYmd, endYmd, options) {
  if (!startYmd || !endYmd) return [];
  return eachBookingRangeYmd(startYmd, endYmd).filter((ymd) =>
    isBookingDateUnavailable(ymd, { ...options, monthLoading: false })
  );
}
